<template>
  <div
    id="grating-wallpaper"
    ref="gratingWallpaperRef"
    class="grating-wallpaper"
    data-position="0"
    data-name="光栅页面流量"
  >
    <Swiper
      ref="swiperRef"
      :modules="swiperModules"
      v-bind="swiperConfig"
      @slideChange="onSlideChange"
      class="grating-wallpaper-swiper"
    >
      <SwiperSlide
        v-for="(option, index) in swiperOptions"
        :key="option.id"
        class="grating-wallpaper-slide"
      >
        <!-- 背景图片 -->
        <PlusImage
          v-if="!isPC"
          class="grating-wallpaper-background"
          :src="deviceBackground(option.id)"
        />
        <video
          v-else
          :ref="`video${index}`"
          class="grating-wallpaper-background"
          :src="VIDEO[`gratingWallpaper${option.id}`]"
          preload="metadata"
          muted
          autoplay
          @ended="onVideoEnded"
        ></video>
        <p
          v-if="isMobile"
          class="grating-wallpaper-title-head-left ffm600 title-text"
        >
          趣味
        </p>
        <p
          v-if="isMobile"
          class="grating-wallpaper-title-head-right ffm600 title-text"
        >
          光栅
        </p>
        <PlusImage
          v-if="isMobile"
          class="grating-wallpaper-phone-image"
          :src="GRATING_WALLPAPER[`phone${option.id}Pad`]"
        />
      </SwiperSlide>
    </Swiper>
    <div class="grating-wallpaper-content-container">
      <p class="grating-wallpaper-content-title ffm600">谁玩都上头</p>
      <div class="grating-wallpaper-content-text-container ffm400">
        <p class="grating-wallpaper-content-text">轻轻一歪，锁屏变脸！</p>
        <p class="grating-wallpaper-content-text">
          每个角度都是新惊喜，越玩越上瘾。
        </p>
      </div>
    </div>

    <div
      class="swiper-control-container"
      :style="{ '--control-bg-color': currentControlBg }"
    >
      <div
        v-for="(option, index) in swiperOptions"
        :key="option.id"
        :class="[
          'swiper-control-text ffm400',
          { selected: index === currentIndex },
          option.id,
        ]"
        @click="switchSwiper(index)"
      >
        {{ option.text }}
      </div>
    </div>
  </div>
</template>

<script>
import { GRATING_WALLPAPER } from "@/config/image";
import VIDEO from "@/config/video";
import { initExposureTrack } from "@/utils/track.js";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination } from "swiper/modules";
import { mapGetters } from "vuex";
import { gsap, ScrollTrigger } from "@/utils/gsapConfig.js";
import "swiper/css";
import "swiper/css/pagination";
import PlusImage from "@/components/PlusImage.vue";

export default {
  name: "GratingWallpaper",
  components: {
    Swiper,
    SwiperSlide,
    PlusImage,
  },
  data() {
    return {
      GRATING_WALLPAPER,
      VIDEO,
      currentIndex: 0,
      intersectionObserver: null,
      isInViewport: false,
      scrollTriggerInstance: null,
      swiperOptions: [
        {
          id: "Orange",
          text: "视频效果",
          wallpaper: "wallpaperOrange",
          phone: "phoneOrange",
          controlBg: "rgba(247, 237, 221, 1)",
        },
        {
          id: "Grey",
          text: "Live图效果",
          wallpaper: "wallpaperGrey",
          phone: "phoneGrey",
          controlBg: "rgba(239, 239, 239, 1)",
        },
        {
          id: "Blue",
          text: "图片效果",
          wallpaper: "wallpaperBlue",
          phone: "phoneBlue",
          controlBg: "rgba(228, 237, 250, 1)",
        },
      ],
      swiperModules: [Pagination],
      swiperConfig: {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        allowTouchMove: false,
        pagination: false,
      },
    };
  },
  props: {},
  computed: {
    ...mapGetters(["isMobile", "isPad", "isPC"]),
    currentControlBg() {
      return this.swiperOptions[this.currentIndex].controlBg;
    },
    deviceBackground() {
      return (id) => {
        if (this.isPad) {
          return this.GRATING_WALLPAPER[`wallpaper${id}Pad`];
        } else {
          return this.GRATING_WALLPAPER[`wallpaper${id}H5`];
        }
      };
    },
  },
  methods: {
    switchSwiper(index) {
      if (index !== this.currentIndex) {
        this.currentIndex = index;
        if (this.$refs.swiperRef) {
          this.$refs.swiperRef.$el.swiper.slideTo(index);
        }
        this.resetAndPlayVideo();
      }
    },
    onSlideChange(swiper) {
      this.currentIndex = swiper.activeIndex;
      this.resetAndPlayVideo();
    },

    onVideoEnded() {
      if (!this.isPC || !this.isInViewport) return;
      this.autoNext();
    },
    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.6, // 当60%的元素可见时触发
      };

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.isInViewport = true;
            if (this.isPC) {
              this.handleViewportChange();
            }
          } else {
            this.isInViewport = false;
            if (this.isPC) {
              this.handleViewportChange();
            }
          }
        });
      }, options);

      const targetElement = this.$el;
      if (targetElement) {
        this.intersectionObserver.observe(targetElement);
      }
    },
    destroyIntersectionObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    },
    autoNext() {
      if (this.$refs.swiperRef) {
        const swiper = this.$refs.swiperRef.$el.swiper;
        const totalSlides = this.swiperOptions.length;

        if (this.currentIndex < totalSlides - 1) {
          swiper.slideNext();
        } else {
          this.currentIndex = 0;
          swiper.slideTo(0);
        }
      }
    },

    controlVideoPlayback(resetTime = true) {
      if (!this.isPC) return;
      let playPromise
      this.$nextTick(() => {
        this.swiperOptions.forEach((option, index) => {
          const video = this.$refs[`video${index}`];
          if (video && video[0]) {
            try {
              if (playPromise !== undefined) {
                playPromise.finally(_ => {
                  video[0].pause();
                })
              }
              if (resetTime) {
                video[0].currentTime = 0;
              }
            } catch (error) {
              console.warn('暂停视频时出错:', error);
            }
          }
        });

        setTimeout(() => {
          const currentVideo = this.$refs[`video${this.currentIndex}`];
          if (currentVideo && currentVideo[0] && this.isInViewport) {
            try {
              if (resetTime) {
                currentVideo[0].currentTime = 0;
              }
              playPromise = currentVideo[0].play();
              if (playPromise !== undefined) {
                playPromise.catch(error => {
                  console.warn('视频播放失败:', error);
                });
              }
            } catch (error) {
              console.warn('播放视频时出错:', error);
            }
          }
        }, 30);
      });
    },

    resetAndPlayVideo() {
      if (!this.isPC) return;
      let playPromise
      this.$nextTick(() => {
        this.swiperOptions.forEach((option, index) => {
          const video = this.$refs[`video${index}`];
          if (video && video[0]) {
            try {
              if (playPromise !== undefined) {
                playPromise.finally(_ => {
                  video[0].pause();
                })
              }
              video[0].currentTime = 0;
            } catch (error) {
              console.warn('暂停视频时出错:', error);
            }
          }
        });

        setTimeout(() => {
          const currentVideo = this.$refs[`video${this.currentIndex}`];
          if (currentVideo && currentVideo[0] && this.isInViewport) {
            try {
              currentVideo[0].currentTime = 0;
              playPromise = currentVideo[0].play();
              if (playPromise !== undefined) {
                playPromise.catch(error => {
                  console.warn('视频播放失败:', error);
                });
              }
            } catch (error) {
              console.warn('播放视频时出错:', error);
            }
          }
        }, 50);
      });
    },

    handleViewportChange() {
      if (!this.isPC) return;
      let playPromise
      const currentVideo = this.$refs[`video${this.currentIndex}`];
      if (currentVideo && currentVideo[0]) {
        try {
          if (this.isInViewport) {
            playPromise = currentVideo[0].play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.warn('视频播放失败:', error);
              });
            }
          } else {
            if (playPromise !== undefined) {
              playPromise.finally(_ => {
                currentVideo[0].pause();
              })
            }
          }
        } catch (error) {
          console.warn('处理视口变化时出错:', error);
        }
      }
    },

    initScrollTrigger() {
      if (!this.isPC) return;
      const container = this.$refs.gratingWallpaperRef;
      if (!container) return;

      if (this.scrollTriggerInstance) {
        this.scrollTriggerInstance.kill();
      }

      this.scrollTriggerInstance = ScrollTrigger.create({
        // markers: true,
        trigger: container,
        start: "top top+=84",
        end: "+=" + container.offsetHeight,
        pin: true,
      });
    },

    destroyScrollTrigger() {
      if (this.scrollTriggerInstance) {
        this.scrollTriggerInstance.kill();
        this.scrollTriggerInstance = null;
      }
    },
  },
  mounted() {
    initExposureTrack(".grating-wallpaper")();
    if (this.isPC) {
      this.setupIntersectionObserver();
      this.resetAndPlayVideo();
    }
  },
  beforeDestroy() {
    this.destroyIntersectionObserver();
    this.destroyScrollTrigger();
  },
};
</script>

<style lang="scss" scoped>
:global(.pin-spacer:has(.grating-wallpaper)) {
  background: #000000 !important;
}
.grating-wallpaper {
  padding-top: 86px;
  height: 900 * $px1920vw + vw;
  position: relative;
  overflow: hidden;
}
.grating-wallpaper {
  display: flex;
  flex-direction: column;
  align-items: center;

  .grating-wallpaper-swiper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .grating-wallpaper-slide {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .grating-wallpaper-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    z-index: 0;
  }

  .title-text {
    top: 233 * $px1920vw + vw;
    position: absolute;
    font-size: 160 * $px1920vw + vw;
    line-height: 160 * $px1920vw + vw;
    letter-spacing: 0.2em;
    background: linear-gradient(
      180deg,
      #ffffff 0%,
      rgba(255, 255, 255, 0.5) 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    z-index: 1;
  }
  .grating-wallpaper-title-head-left {
    left: 479 * $px1920vw + vw;
  }
  .grating-wallpaper-title-head-right {
    left: 1089 * $px1920vw + vw;
  }
  .grating-wallpaper-phone-image {
    top: 84 * $px1920vw + vw;
    position: absolute;
    width: 174 * $px1920vw + vw;
    height: 459.71 * $px1920vw + vw;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
  }
  .grating-wallpaper-content-container {
    bottom: 140 * $px1920vw + vw;
    position: absolute;
    z-index: 1;
    .grating-wallpaper-content-title {
      /* H1_64号/一级标题(pc) */
      font-size: 64 * $px1920vw + vw;
      line-height: 76.8 * $px1920vw + vw;
      letter-spacing: normal;
      color: #000000;
    }
    .grating-wallpaper-content-text-container {
      margin-top: 24 * $px1920vw + vw;
      /* B_18号/正文(pc) */
      font-size: 18 * $px1920vw + vw;
      line-height: 25.2 * $px1920vw + vw;
      text-align: center;
      letter-spacing: normal;
      color: rgba(0, 0, 0, 0.6);
    }
  }
  .swiper-control-container {
    width: 341 * $px1920vw + vw;
    height: 60 * $px1920vw + vw;
    bottom: 48 * $px1920vw + vw;
    position: absolute;
    border-radius: 284 * $px1920vw + vw;
    background: linear-gradient(
      180deg,
      #ffffff 0%,
      rgba(255, 255, 255, 0) 46%,
      #ffffff 95%
    );
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    z-index: 1;
    &::before {
      content: "";
      position: absolute;
      top: 1px;
      left: 1px;
      right: 1px;
      bottom: 1px;
      background: var(--control-bg-color, rgba(228, 237, 250, 1));
      border-radius: 284 * $px1920vw + vw;
      transition: background-color 0.3s ease;
    }
    .swiper-control-text {
      /* B_18号/正文(pc) */
      font-size: 18 * $px1920vw + vw;
      line-height: 25.2 * $px1920vw + vw;
      letter-spacing: normal;
      color: #000000;
      z-index: 1;
      position: relative;
      cursor: pointer;
      transition: color 0.3s ease;

      &:hover {
        color: rgba(0, 0, 0, 0.75);
      }

      &.selected {
        &::before {
          content: "";
          position: absolute;
          top: -(9 * $px1920vw + vw);
          left: -(16 * $px1920vw + vw);
          right: -(16 * $px1920vw + vw);
          bottom: -(9 * $px1920vw + vw);
          border-radius: 284 * $px1920vw + vw;
          background: #ffffff;
          z-index: -1;
        }
      }
    }
  }
}

// pad端
.isPad {
  .grating-wallpaper {
    height: 600 * $px676vw + vw;
    .title-text {
      top: 153 * $px676vw + vw;
      font-size: 80 * $px676vw + vw;
      line-height: normal;
      letter-spacing: 0.16em;
      text-align: center;
    }
    .grating-wallpaper-title-head-left {
      left: 86 * $px676vw + vw;
    }
    .grating-wallpaper-title-head-right {
      left: 417 * $px676vw + vw;
    }
    .grating-wallpaper-phone-image {
      top: 33 * $px676vw + vw;
      width: 130 * $px676vw + vw;
      height: 343.46 * $px676vw + vw;
    }
    .grating-wallpaper-content-container {
      bottom: 128 * $px676vw + vw;
      .grating-wallpaper-content-title {
        font-size: 25 * $px676vw + vw;
        line-height: normal;
      }
      .grating-wallpaper-content-text-container {
        margin-top: 8 * $px676vw + vw;
        font-size: 12 * $px676vw + vw;
        line-height: 16 * $px676vw + vw;
      }
    }
    .swiper-control-container {
      width: 320 * $px676vw + vw;
      height: 56 * $px676vw + vw;
      bottom: 36 * $px676vw + vw;
      border-radius: 266.51 * $px676vw + vw;
      background: linear-gradient(
        180deg,
        #ffffff 0%,
        rgba(255, 255, 255, 0) 46%,
        #ffffff 95%,
        0.94
      );
      &::before {
        border-radius: 266.51 * $px676vw + vw;
      }
      .swiper-control-text {
        font-size: 16.89 * $px676vw + vw;
        line-height: 23.65 * $px676vw + vw;
        &.selected {
          &::before {
            top: -(8.4 * $px676vw + vw);
            left: -(15 * $px676vw + vw);
            right: -(15 * $px676vw + vw);
            bottom: -(8.4 * $px676vw + vw);
            border-radius: 266.51 * $px676vw + vw;
          }
        }
      }
    }
  }
}

// 手机端
.isMobile {
  .grating-wallpaper {
    height: 530 * $px360vw + vw;
    .title-text {
      top: 135.46 * $px360vw + vw;
      font-size: 59.52 * $px360vw + vw;
      line-height: normal;
      letter-spacing: 0.06em;
      text-align: center;
    }
    .grating-wallpaper-title-head-left {
      left: 18.66 * $px360vw + vw;
    }
    .grating-wallpaper-title-head-right {
      left: 218.34 * $px360vw + vw;
      white-space: nowrap;
    }
    .grating-wallpaper-phone-image {
      top: 36 * $px360vw + vw;
      width: 106 * $px360vw + vw;
      height: 280.05 * $px360vw + vw;
    }
    .grating-wallpaper-content-container {
      bottom: 122 * $px360vw + vw;
      .grating-wallpaper-content-title {
        font-size: 25 * $px360vw + vw;
        line-height: normal;
      }
      .grating-wallpaper-content-text-container {
        margin-top: 8 * $px360vw + vw;
        font-size: 12 * $px360vw + vw;
        line-height: 16 * $px360vw + vw;
      }
    }
    .swiper-control-container {
      width: 320 * $px360vw + vw;
      height: 56 * $px360vw + vw;
      bottom: 36 * $px360vw + vw;
      border-radius: 266.51 * $px360vw + vw;
      background: linear-gradient(
        180deg,
        #ffffff 0%,
        rgba(255, 255, 255, 0) 46%,
        #ffffff 95%,
        0.94
      );
      &::before {
        border-radius: 266.51 * $px360vw + vw;
      }
      .swiper-control-text {
        font-size: 16.89 * $px360vw + vw;
        line-height: 23.65 * $px360vw + vw;
        &.selected {
          &::before {
            top: -(8.4 * $px360vw + vw);
            left: -(15 * $px360vw + vw);
            right: -(15 * $px360vw + vw);
            bottom: -(8.4 * $px360vw + vw);
            border-radius: 266.51 * $px360vw + vw;
          }
        }
      }
    }
  }
}
</style>